/**
 * Build a self-contained HTML document for preview, download, or print-to-PDF.
 * All user-controlled fields are escaped for safe use in srcDoc / blob URLs.
 */

function esc(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function formatMoneyZAR(n) {
  const num = typeof n === 'number' && !Number.isNaN(n) ? n : parseFloat(String(n || 0)) || 0;
  return `R ${num.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-ZA', { year: 'numeric', month: 'long', day: 'numeric' });
  } catch {
    return esc(iso);
  }
}

const DEFAULT_BRAND = {
  companyName: 'Fortitude IT Solutions',
  tagline: 'Enterprise software, web, and design — delivered with precision.',
};

export function buildOfficialDocumentHtml(doc, branding = {}) {
  const company = esc(branding.companyName || DEFAULT_BRAND.companyName);
  const tagline = esc(branding.tagline || DEFAULT_BRAND.tagline);
  const title = esc(doc?.name || 'Official document');
  const docType = esc(doc?.type || 'Document');
  const recipient = esc(doc?.recipient || '—');
  const status = esc(doc?.status || '—');
  const description = esc(doc?.description || '—');
  const amountLine =
    doc?.amount != null && String(doc.amount).trim() !== '' && Number(doc.amount) !== 0
      ? formatMoneyZAR(doc.amount)
      : '—';
  const issueDate = formatDate(doc?.date);
  const approvals = esc(String(doc?.approvals ?? 0));
  const createdBy = esc(doc?.createdByName || '—');

  const bodyHtml = `
    <div class="doc-meta-grid">
      <div class="doc-meta"><span class="k">Document type</span><span class="v">${docType}</span></div>
      <div class="doc-meta"><span class="k">Status</span><span class="v status">${status}</span></div>
      <div class="doc-meta"><span class="k">Recipient</span><span class="v">${recipient}</span></div>
      <div class="doc-meta"><span class="k">Issue date</span><span class="v">${esc(issueDate)}</span></div>
      <div class="doc-meta"><span class="k">Amount</span><span class="v">${esc(amountLine)}</span></div>
      <div class="doc-meta"><span class="k">Founder approvals</span><span class="v">${approvals} / 2</span></div>
      <div class="doc-meta full"><span class="k">Prepared by</span><span class="v">${createdBy}</span></div>
    </div>
    <section class="doc-section">
      <h2>Summary / notes</h2>
      <p class="doc-notes">${description}</p>
    </section>
    <section class="doc-section muted">
      <p class="fine-print">This document was generated from the Fortitude internal portal. For queries, contact your account manager or <strong>admin@fitss.co.za</strong>. A printable PDF can be produced from your browser via <em>Print → Save as PDF</em>.</p>
    </section>
  `;

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8"/>
  <meta name="viewport" content="width=device-width, initial-scale=1"/>
  <title>${title}</title>
  <style>
    @page { margin: 18mm; }
    * { box-sizing: border-box; }
    body {
      margin: 0;
      font-family: Georgia, 'Times New Roman', serif;
      color: #1a1a1a;
      background: #faf8f4;
      line-height: 1.55;
    }
    .sheet {
      max-width: 800px;
      margin: 0 auto;
      padding: 36px 40px 48px;
      background: #fff;
      border: 1px solid #e8e0d0;
      box-shadow: 0 12px 40px rgba(0,0,0,0.06);
    }
    .brand-bar {
      display: flex;
      align-items: flex-end;
      justify-content: space-between;
      gap: 16px;
      padding-bottom: 20px;
      margin-bottom: 24px;
      border-bottom: 3px solid #c9a227;
    }
    .brand-name {
      font-family: system-ui, -apple-system, Segoe UI, Roboto, sans-serif;
      font-size: 1.35rem;
      font-weight: 800;
      letter-spacing: 0.12em;
      text-transform: uppercase;
      color: #111;
    }
    .brand-sub {
      font-family: system-ui, -apple-system, Segoe UI, Roboto, sans-serif;
      font-size: 0.82rem;
      color: #666;
      margin-top: 6px;
      max-width: 420px;
    }
    .doc-title {
      font-family: system-ui, -apple-system, Segoe UI, Roboto, sans-serif;
      font-size: 1.65rem;
      font-weight: 700;
      margin: 0 0 8px;
      color: #0f172a;
    }
    .doc-meta-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 12px 20px;
      margin-top: 22px;
    }
    .doc-meta {
      padding: 12px 14px;
      background: #f8f6f1;
      border: 1px solid #ebe5d8;
      border-radius: 8px;
    }
    .doc-meta.full { grid-column: 1 / -1; }
    .doc-meta .k {
      display: block;
      font-family: system-ui, sans-serif;
      font-size: 0.68rem;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.08em;
      color: #927b35;
      margin-bottom: 4px;
    }
    .doc-meta .v {
      font-size: 0.95rem;
      font-weight: 600;
      color: #222;
    }
    .doc-meta .v.status { color: #0f5132; }
    .doc-section {
      margin-top: 28px;
      font-family: system-ui, sans-serif;
    }
    .doc-section h2 {
      font-size: 0.78rem;
      text-transform: uppercase;
      letter-spacing: 0.1em;
      color: #927b35;
      margin: 0 0 10px;
    }
    .doc-notes {
      margin: 0;
      white-space: pre-wrap;
      font-size: 0.95rem;
      color: #333;
    }
    .doc-section.muted { margin-top: 32px; }
    .fine-print {
      font-size: 0.78rem;
      color: #666;
      margin: 0;
      line-height: 1.6;
    }
    @media print {
      body { background: #fff; }
      .sheet { box-shadow: none; border: none; max-width: none; padding: 0; }
    }
  </style>
</head>
<body>
  <div class="sheet">
    <header class="brand-bar">
      <div>
        <div class="brand-name">${company}</div>
        <div class="brand-sub">${tagline}</div>
      </div>
    </header>
    <h1 class="doc-title">${title}</h1>
    ${bodyHtml}
  </div>
</body>
</html>`;
}

export function downloadOfficialDocumentHtml(doc, branding, fileBaseName = 'document') {
  const html = buildOfficialDocumentHtml(doc, branding);
  const safe = String(fileBaseName || 'document').replace(/[^\w\-]+/g, '_').slice(0, 80);
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${safe}.html`;
  a.click();
  URL.revokeObjectURL(url);
}

export function printOfficialDocumentHtml(doc, branding) {
  const html = buildOfficialDocumentHtml(doc, branding);
  const w = window.open('', '_blank', 'noopener,noreferrer,width=900,height=1200');
  if (!w) {
    throw new Error('Pop-up blocked — allow pop-ups to print this document.');
  }
  w.document.open();
  w.document.write(html);
  w.document.close();
  w.focus();
  setTimeout(() => {
    try {
      w.print();
    } catch {
      /* ignore */
    }
  }, 250);
}
